import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { SALayout } from "../../components/superadmin/SALayout";
import { formatCurrency, formatDate } from "../../lib/format";
import { Search } from "lucide-react";

const STATUS_OPTS = ["All", "Trial", "Active", "Paused", "Churned"];
const PLAN_OPTS = ["All", "Trial", "Pro", "Growth", "Enterprise"];

export default function SAVenueDirectory() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [plan, setPlan] = useState("All");
  const [sort, setSort] = useState("name");

  useEffect(() => {
    api.get("/admin/venues").then(({ data }) => {
      setVenues(data);
      setLoading(false);
    });
  }, []);

  const rows = useMemo(() => {
    let r = [...venues];
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      r = r.filter(
        (v) =>
          (v.name || "").toLowerCase().includes(n) ||
          (v.city || "").toLowerCase().includes(n) ||
          (v.venue_code || "").toLowerCase().includes(n),
      );
    }
    if (status !== "All") r = r.filter((v) => v.status === status);
    if (plan !== "All") r = r.filter((v) => v.plan === plan);
    r.sort((a, b) => {
      if (sort === "name") return (a.name || "").localeCompare(b.name || "");
      if (sort === "health") return (b.health_score || 0) - (a.health_score || 0);
      if (sort === "onboarded") return (b.onboarded_at || "").localeCompare(a.onboarded_at || "");
      if (sort === "mrr") return (b.monthly_amount || 0) - (a.monthly_amount || 0);
      return 0;
    });
    return r;
  }, [venues, q, status, plan, sort]);

  return (
    <SALayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">Venue Directory</h1>
        <div className="text-xs text-[#A0A0A0]">{rows.length} of {venues.length} venues</div>
      </div>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl p-4 mb-6 grid md:grid-cols-4 gap-3">
        <div className="relative md:col-span-2">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0A0]" />
          <input
            data-testid="sa-venues-search"
            placeholder="Search name, city, code…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-[#A0A0A0] focus:border-[#9D00FF]"
          />
        </div>
        <select
          data-testid="sa-venues-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white"
        >
          {STATUS_OPTS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select
          data-testid="sa-venues-plan-filter"
          value={plan}
          onChange={(e) => setPlan(e.target.value)}
          className="bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white"
        >
          {PLAN_OPTS.map((p) => <option key={p}>{p}</option>)}
        </select>
      </div>

      <div className="bg-[#1A1A1A] border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="sa-venues-table">
            <thead className="bg-[#0F0F0F] text-[#A0A0A0] text-xs uppercase tracking-wider">
              <tr>
                <Th label="Name" k="name" sort={sort} setSort={setSort} />
                <th className="px-4 py-3 text-left">City</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-left">Status</th>
                <Th label="Health" k="health" sort={sort} setSort={setSort} />
                <Th label="Onboarded" k="onboarded" sort={sort} setSort={setSort} />
                <Th label="MRR" k="mrr" sort={sort} setSort={setSort} />
                <th className="px-4 py-3 text-left">Transfer</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {loading && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-[#A0A0A0]">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-6 text-center text-[#A0A0A0]">No venues match.</td></tr>
              )}
              {rows.map((v) => (
                <tr key={v.id} className="hover:bg-white/5">
                  <td className="px-4 py-3 font-semibold text-white">
                    <div>{v.name}</div>
                    <div className="text-[10px] text-[#A0A0A0] uppercase tracking-wider">{v.venue_code || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-white/80">{v.city || "—"}</td>
                  <td className="px-4 py-3"><PlanChip plan={v.plan} /></td>
                  <td className="px-4 py-3"><StatusChip status={v.status} /></td>
                  <td className="px-4 py-3 text-white/80">
                    <div className="flex items-center gap-2">
                      <span>{v.health_label}</span>
                      <span className="text-[11px] text-[#A0A0A0]">({v.health_score ?? 0}%)</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white/80">{formatDate(v.onboarded_at)}</td>
                  <td className="px-4 py-3 text-white/80">{formatCurrency(v.monthly_amount)}</td>
                  <td className="px-4 py-3">
                    {v.pending_transfer ? (
                      <span
                        title={`Pending transfer to ${v.pending_transfer}`}
                        data-testid={`sa-venue-transfer-pending-${v.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-500/15 border border-amber-500/40 text-amber-400 rounded-full px-2 py-0.5"
                      >
                        🟡 Pending
                      </span>
                    ) : (
                      <span className="text-[#3A3A3A] text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/venues/${v.id}`}
                      data-testid={`sa-venue-open-${v.id}`}
                      className="text-[#9D00FF] font-semibold hover:underline text-xs"
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SALayout>
  );
}

function Th({ label, k, sort, setSort }) {
  const active = sort === k;
  return (
    <th className="px-4 py-3 text-left">
      <button
        onClick={() => setSort(k)}
        className={`text-xs uppercase tracking-wider ${active ? "text-white" : "text-[#A0A0A0]"} hover:text-white`}
        data-testid={`sa-venues-sort-${k}`}
      >
        {label}{active ? " ↓" : ""}
      </button>
    </th>
  );
}

function PlanChip({ plan }) {
  const map = {
    Trial: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40",
    Pro: "bg-[#9D00FF]/15 text-[#D78CFF] border-[#9D00FF]/40",
    Growth: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    Enterprise: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${map[plan] || "bg-white/10 text-white border-white/20"}`}>{plan || "—"}</span>;
}
function StatusChip({ status }) {
  const map = {
    Active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    Trial: "bg-[#F59E0B]/15 text-[#F59E0B] border-[#F59E0B]/40",
    Paused: "bg-white/10 text-white border-white/20",
    Churned: "bg-[#EF4444]/15 text-[#EF4444] border-[#EF4444]/40",
  };
  return <span className={`text-[10px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${map[status] || "bg-white/10 text-white border-white/20"}`}>{status || "—"}</span>;
}
